import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { ExameImagem } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import { calcularCamposAlterados } from "../../../shared/utils/trilhaAuditoriaDiff";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

interface ExamesImagemContextProps {
  exames: ExameImagem[];
  loading: boolean;
  adicionarExame: (
    pacienteId: string,
    dados: Omit<ExameImagem, "id" | "registradoPor" | "registradoPorNome">
  ) => Promise<void>;
  listarExamesPorPaciente: (pacienteId: string) => ExameImagem[];
  editarExame: (id: string, dados: Partial<ExameImagem>) => Promise<void>;
  removerExame: (id: string) => Promise<void>;
  carregarExames: () => Promise<void>;
}

const ExamesImagemContext = createContext<ExamesImagemContextProps | undefined>(
  undefined
);

export function ExamesImagemProvider({ children }: { children: ReactNode }) {
  console.log("🔥 ExamesImagemProvider montado!"); // ← ADICIONA
  const { usuarioLogado } = useAuth();
  const [exames, setExames] = useState<ExameImagem[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // 📥 CARREGAR EXAMES DO FIREBASE
  // ============================================
  const carregarExames = async () => {
    try {
      setLoading(true);
      console.log("🔍 Carregando exames de imagem do Firebase...");

      const querySnapshot = await getDocs(collection(db, "exames-imagem"));

      const examesFormatados: ExameImagem[] = querySnapshot.docs.map(
        (docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            pacienteId: data.pacienteId,
            dataExame:
              data.dataExame?.toDate?.()?.toISOString?.() || data.dataExame,
            tipoExame: data.tipoExame || data.tipo,
            laudo: data.laudo,
            observacoes: data.observacoes,
            registradoPor: data.registradoPor,
            registradoPorNome: data.registradoPorNome,
            editadoPor: data.editadoPor,
            editadoPorNome: data.editadoPorNome,
            dataEdicao:
              data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
            // ✅ Achados da IA
            achados: data.achados || null,
            resumoAchados: data.resumoAchados || "",
            analiseCompleta: data.analiseCompleta || "",
          } as ExameImagem;
        }
      );

      setExames(examesFormatados);
      console.log("✅ Exames de imagem carregados:", examesFormatados.length);
    } catch (error) {
      console.error("❌ Erro ao carregar exames de imagem:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 CARREGAR AO MONTAR
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("🔍 Escutando exames de imagem em tempo real...");

    const unsubscribe = onSnapshot(
      collection(db, "exames-imagem"),
      (snapshot) => {
        const lista: ExameImagem[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            pacienteId: data.pacienteId,
            dataExame:
              data.dataExame?.toDate?.()?.toISOString?.() || data.dataExame,
            tipoExame: data.tipoExame || data.tipo,
            laudo: data.laudo,
            observacoes: data.observacoes,
            registradoPor: data.registradoPor,
            registradoPorNome: data.registradoPorNome,
            editadoPor: data.editadoPor,
            editadoPorNome: data.editadoPorNome,
            dataEdicao:
              data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
            achados: data.achados || null,
            resumoAchados: data.resumoAchados || "",
            analiseCompleta: data.analiseCompleta || "",
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
          } as unknown as ExameImagem;
        });

        setExames(lista);
        setLoading(false);
        console.log(
          "✅ Exames de imagem atualizados (tempo real):",
          lista.length
        );
      },
      (error) => {
        console.error("❌ Erro no listener de exames de imagem:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // ➕ ADICIONAR EXAME
  // ============================================
  const adicionarExame = async (
    pacienteId: string,
    dados: Omit<ExameImagem, "id" | "registradoPor" | "registradoPorNome">
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Adicionando exame de imagem...");

      // Remove campos undefined
      const dadosLimpos = Object.fromEntries(
        Object.entries(dados).filter(([_, value]) => value !== undefined)
      );

      await addDoc(collection(db, "exames-imagem"), {
        pacienteId,
        registradoPor: usuarioLogado.id,
        registradoPorNome: usuarioLogado.nome,
        ...dadosLimpos,
        criadoEm: serverTimestamp(),
      });

      console.log("✅ Exame de imagem adicionado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao adicionar exame de imagem:", error);
      throw error;
    }
  };

  // ============================================
  // 📋 LISTAR EXAMES DE UM PACIENTE
  // ============================================
  const listarExamesPorPaciente = (pacienteId: string): ExameImagem[] => {
    return exames
      .filter((e) => e.pacienteId === pacienteId)
      .sort(
        (a, b) =>
          new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
      );
  };

  // ============================================
  // ✏️ EDITAR EXAME
  // ============================================
  const editarExame = async (id: string, dados: Partial<ExameImagem>) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Editando exame de imagem:", id);

      const dadosFirebase: any = {
        editadoPor: usuarioLogado.id,
        editadoPorNome: usuarioLogado.nome,
        dataEdicao: serverTimestamp(),
      };

      // Adiciona apenas campos que foram alterados.
      // FIX preventivo (AI_99, mesmo bug achado em marcadores/sorologias
      // no teste B213): campos imutáveis do B81/B97 (autoria de upload,
      // criadoEm, caminhoStorage) voltando da tela — `criadoEm` convertido
      // p/ string — fariam a regra negar o update inteiro. Ficam fora do
      // payload.
      const CAMPOS_BLOQUEADOS = new Set([
        "id",
        "registradoPor",
        "registradoPorNome",
        "uploadPor",
        "uploadPorNome",
        "caminhoStorage",
        "criadoEm",
        "dataEdicao",
        "editadoPor",
        "editadoPorNome",
        "pacienteId",
      ]);
      Object.keys(dados).forEach((key) => {
        const value = (dados as any)[key];
        if (value !== undefined && !CAMPOS_BLOQUEADOS.has(key)) {
          dadosFirebase[key] = value;
        }
      });

      await updateDoc(doc(db, "exames-imagem", id), dadosFirebase);

      // B213 — trilha de auditoria
      const atual = exames.find((ex) => ex.id === id);
      await registrarEdicaoTrilha({
        colecao: "exames-imagem",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual
          ? `${atual.tipoExame || "Imagem"}${
              atual.dataExame
                ? ` de ${new Date(atual.dataExame).toLocaleDateString("pt-BR")}`
                : ""
            }`
          : "Imagem",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: calcularCamposAlterados(dados, atual),
      });

      console.log("✅ Exame de imagem editado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao editar exame de imagem:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ REMOVER EXAME
  // ============================================
  const removerExame = async (id: string) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("🗑️ Removendo exame de imagem:", id);

      // B213 — captura antes do delete (a lápide sobrevive ao doc)
      const atual = exames.find((ex) => ex.id === id);

      await deleteDoc(doc(db, "exames-imagem", id));

      await registrarExclusaoTrilha({
        colecao: "exames-imagem",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual
          ? `${atual.tipoExame || "Imagem"}${
              atual.dataExame
                ? ` de ${new Date(atual.dataExame).toLocaleDateString("pt-BR")}`
                : ""
            }`
          : "Imagem",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
      });

      console.log("✅ Exame de imagem removido com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao remover exame de imagem:", error);
      throw error;
    }
  };

  return (
    <ExamesImagemContext.Provider
      value={{
        exames,
        loading,
        adicionarExame,
        listarExamesPorPaciente,
        editarExame,
        removerExame,
        carregarExames,
      }}
    >
      {children}
    </ExamesImagemContext.Provider>
  );
}

export function useExamesImagem() {
  const context = useContext(ExamesImagemContext);
  if (!context) {
    throw new Error(
      "useExamesImagem deve ser usado dentro de ExamesImagemProvider"
    );
  }
  return context;
}
